import React from "react";
import {
    AuditOutlined,
    FileTextOutlined,
    ExperimentOutlined,
    BookOutlined,
    CheckSquareOutlined,
    CameraOutlined,
    UserOutlined,
    TeamOutlined,
    AppstoreOutlined,
    BankOutlined,
    TagsOutlined,
    RightOutlined,
    BranchesOutlined,
    ShopOutlined,
    ApartmentOutlined,
    MessageOutlined,
    IssuesCloseOutlined,
    NotificationOutlined,
    DatabaseOutlined,
    SolutionOutlined,
    FieldTimeOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import type {
    SearchEntityType,
    SearchReferenceKey,
    SearchResultItem,
} from "../../services/apis/search.api";
import { ENTITY_LABELS, ENTITY_LIST_URL } from "./getSearchableEntityTypes";

const ENTITY_ICON: Record<SearchEntityType, React.ReactNode> = {
    audits: <AuditOutlined />,
    assessments: <FileTextOutlined />,
    evaluations: <ExperimentOutlined />,
    anecdotals: <BookOutlined />,
    tasks: <CheckSquareOutlined />,
    imageVerification: <CameraOutlined />,
    people: <UserOutlined />,
    groups: <TeamOutlined />,
    parameters: <TagsOutlined />,
    criteria: <BranchesOutlined />,
    auditCategories: <AppstoreOutlined />,
    organizations: <BankOutlined />,
    templates: <ShopOutlined />,
    departments: <ApartmentOutlined />,
    posts: <MessageOutlined />,
    tickets: <IssuesCloseOutlined />,
    notices: <NotificationOutlined />,
    questionBanks: <DatabaseOutlined />,
    students: <SolutionOutlined />,
    oneTimeAudits: <FieldTimeOutlined />,
};

const REFERENCE_LABELS: Record<SearchReferenceKey, string> = {
    auditsAsAuditor: "Audits (as auditor)",
    auditsAsAuditee: "Audits (as auditee)",
    tasksAssigned: "Tasks assigned",
    tasksCreated: "Tasks created",
    imageVerifications: "Image verifications",
    groups: "Groups",
    anecdotalsAsObserver: "Anecdotal records (observer)",
    anecdotalsAsAuditee: "Anecdotal records (auditee)",
    assessmentsInvited: "Assessments (invited)",
    assessmentsCreated: "Assessments (created)",
    evaluationsAssigned: "Evaluations (assigned)",
    evaluationsCreated: "Evaluations (created)",
    postsCreated: "Posts",
    postsCommented: "Commented on posts",
    postsLiked: "Reacted to posts",
    ticketsRaised: "Tickets raised",
    ticketsCommented: "Ticket comments",
    audits: "Audits",
    auditForms: "Audit forms",
    tasks: "Tasks",
    assessments: "Assessments",
    evaluations: "Evaluations",
    anecdotals: "Anecdotal records",
    auditCategories: "Audit categories",
    parameters: "Parameters",
    criteria: "Criteria",
};

function escapeRegex(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Wrap case-insensitive matches of `query` in <mark> for visual emphasis. */
function highlight(text: string | undefined, query: string): React.ReactNode {
    if (!text) return text ?? null;
    const q = (query || "").trim();
    if (!q) return text;
    const rx = new RegExp(`(${escapeRegex(q)})`, "ig");
    const parts = text.split(rx);
    return parts.map((part, i) =>
        i % 2 === 1 ? (
            <mark key={i} className="cmdp__mark">
                {part}
            </mark>
        ) : (
            <React.Fragment key={i}>{part}</React.Fragment>
        )
    );
}

interface Props {
    type: SearchEntityType;
    items: SearchResultItem[];
    total: number;
    activeIndex: number;
    baseIndex: number;
    query: string;
    onSelect: (item: SearchResultItem, type: SearchEntityType) => void;
    onClose: () => void;
}

const CommandPaletteResultGroup: React.FC<Props> = ({
    type,
    items,
    total,
    activeIndex,
    baseIndex,
    query,
    onSelect,
    onClose,
}) => {
    const navigate = useNavigate();
    if (items.length === 0) return null;

    const viewAll = () => {
        const listUrl = ENTITY_LIST_URL[type];
        navigate(
            query ? `${listUrl}?q=${encodeURIComponent(query)}` : listUrl
        );
        onClose();
    };

    return (
        <div className="cmdp__group" role="group" aria-label={ENTITY_LABELS[type]}>
            <div className="cmdp__group-header">
                <span className="cmdp__group-title">{ENTITY_LABELS[type]}</span>
                <span className="cmdp__group-count">{total}</span>
            </div>
            <ul className="cmdp__group-list">
                {items.map((item, i) => {
                    const globalIndex = baseIndex + i;
                    const isActive = globalIndex === activeIndex;
                    const refs = item.references || [];
                    return (
                        <li
                            key={item._id}
                            className={`cmdp__row cmdp__row--stack${isActive ? " cmdp__row--active" : ""}`}
                            data-index={globalIndex}
                        >
                            <div
                                className="cmdp__row-main"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    onSelect(item, type);
                                }}
                            >
                                <span className="cmdp__row-icon">
                                    {ENTITY_ICON[type]}
                                </span>
                                <span className="cmdp__row-body">
                                    <span className="cmdp__row-title">
                                        {highlight(item.title, query)}
                                    </span>
                                    {item.subtitle && (
                                        <span className="cmdp__row-sub">
                                            {highlight(item.subtitle, query)}
                                        </span>
                                    )}
                                </span>
                                <RightOutlined className="cmdp__row-arrow" />
                            </div>
                            {refs.length > 0 && (
                                <div className="cmdp__refs">
                                    {refs.map((r) => (
                                        <div
                                            key={`${item._id}-${r.key}`}
                                            className="cmdp__ref-group"
                                        >
                                            <div className="cmdp__ref-group-head">
                                                <span className="cmdp__ref-group-title">
                                                    {REFERENCE_LABELS[r.key]}
                                                </span>
                                                <span className="cmdp__ref-group-count">
                                                    {r.total}
                                                </span>
                                            </div>
                                            <ul className="cmdp__ref-list">
                                                {r.items.map((sub) => (
                                                    <li
                                                        key={sub._id}
                                                        className="cmdp__ref-item"
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            if (
                                                                sub.openTaskId
                                                            ) {
                                                                navigate(
                                                                    sub.url,
                                                                    {
                                                                        state: {
                                                                            openTaskId:
                                                                                sub.openTaskId,
                                                                        },
                                                                    }
                                                                );
                                                            } else {
                                                                navigate(sub.url);
                                                            }
                                                            onClose();
                                                        }}
                                                        title={sub.title}
                                                    >
                                                        <span className="cmdp__ref-item-title">
                                                            {sub.title}
                                                        </span>
                                                        {sub.subtitle && (
                                                            <span className="cmdp__ref-item-sub">
                                                                {sub.subtitle}
                                                            </span>
                                                        )}
                                                        <RightOutlined className="cmdp__ref-item-arrow" />
                                                    </li>
                                                ))}
                                            </ul>
                                            {r.listUrl &&
                                                r.total > r.items.length && (
                                                    <button
                                                        type="button"
                                                        className="cmdp__ref-seeall"
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            navigate(r.listUrl!);
                                                            onClose();
                                                        }}
                                                    >
                                                        See all {r.total}
                                                    </button>
                                                )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>
            {total > items.length && (
                <button
                    type="button"
                    className="cmdp__group-seeall"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        viewAll();
                    }}
                >
                    See all {total} in {ENTITY_LABELS[type]}
                </button>
            )}
        </div>
    );
};

export default CommandPaletteResultGroup;
